import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { GatewayRtkConfig } from '@wheere/api-gateway';

/**
 * RTK status card for the integrator gateway detail page. Shows mode (AUTO/MANUAL), pinned
 * base id (when MANUAL), and the latest fix metadata if available.
 *
 * <p>Read-only on the integrator side — switching mode (AUTO ↔ MANUAL) is reserved for
 * inside / PLATFORM_*. The component displays {@code rtkConfig.rtkMode} and
 * {@code rtkConfig.rtkBaseId} as bare info chips.
 */
@Component({
  selector: 'lib-rtk-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3">
      <header class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-gray-700">RTK</h3>
        <span class="rounded-full px-2 py-0.5 text-xs font-medium" [class]="modeClasses()">
          {{ mode() }}
        </span>
      </header>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div class="rounded border border-gray-200 bg-gray-50 p-3">
          <div class="text-xs uppercase tracking-wide text-gray-500">Base ID</div>
          <div class="mt-1 font-mono text-sm text-gray-700">{{ baseLabel() }}</div>
        </div>
        <div class="rounded border border-gray-200 bg-gray-50 p-3">
          <div class="text-xs uppercase tracking-wide text-gray-500">Notes</div>
          <div class="mt-1 text-xs text-gray-600">
            @if (mode() === 'AUTO') {
              The nearest active Centipede base is selected on every GNSS fix.
            } @else if (mode() === 'MANUAL') {
              A specific base is pinned by the operator. Contact Wheere admin to change it.
            } @else {
              RTK mode unknown — ask the gateway to re-sync.
            }
          </div>
        </div>
      </div>
    </div>
  `,
})
export class RtkCardComponent {
  rtkConfig = input.required<GatewayRtkConfig | null>();

  protected readonly mode = computed(() => this.rtkConfig()?.mode ?? 'UNKNOWN');

  protected readonly baseLabel = computed(() => this.rtkConfig()?.baseId ?? '—');

  protected readonly modeClasses = computed(() => {
    switch (this.mode()) {
      case 'AUTO':
        return 'bg-aquamarine-50 text-aquamarine-900';
      case 'MANUAL':
        return 'bg-pacificBlue-50 text-pacificBlue-900';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  });
}
